import {
   IOrgContext,
   ISampleLead,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'

/**
 * Variables are written in the UI as `{first name}`. The library's templates
 * use the engine's names, `{lead_firstname}` (seen on production: every
 * script and text in the catalog). `toTemplate` / `fromTemplate` convert;
 * `fromTemplate` also reads the handoff's `{{first_name}}` and older aliases.
 * Any other `{token}` is left as it is, so nothing the library says is lost.
 */
export const VARIABLES = [
   {
      label: 'first name',
      token: 'lead_firstname',
      aliases: ['first_name', 'lead_first_name'],
   },
   { label: 'assistant name', token: 'assistant_name', aliases: [] },
   {
      label: 'organization',
      token: 'organization_name',
      aliases: ['company', 'brokerage_name'],
   },
   { label: 'agent name', token: 'agent_name', aliases: ['agent_firstname'] },
   { label: 'city', token: 'city', aliases: [] },
] as const

export type TVariableLabel = (typeof VARIABLES)[number]['label']

export const toTemplate = (text: string): string =>
   VARIABLES.reduce(
      (s, v) => s.split(`{${v.label}}`).join(`{${v.token}}`),
      text
   )

/*
 * Lossless both ways: only the engine's own token for each label is read as
 * that label; an alias is read only in the handoff's `{{alias}}` form. So
 * `{agent_firstname}` or `{company}` in a library section survive a round
 * trip untouched, which the backend checks for its fixed sections.
 */
export const fromTemplate = (template: string): string =>
   VARIABLES.reduce((s, v) => {
      /* The double-brace form first, or its inner braces would be read as the engine's token. */
      let out = s
      for (const token of [v.token, ...v.aliases])
         out = out.split(`{{${token}}}`).join(`{${v.label}}`)
      return out.split(`{${v.token}}`).join(`{${v.label}}`)
   }, template)

/** Splits text into plain runs and variable runs, for rendering variables as chips. */
export const splitVariables = (
   text: string
): Array<{ text: string; variable: boolean }> => {
   const out: Array<{ text: string; variable: boolean }> = []
   const re = /\{([a-z ]+)\}/g
   let last = 0
   let m: RegExpExecArray | null
   while ((m = re.exec(text))) {
      if (m.index > last)
         out.push({ text: text.slice(last, m.index), variable: false })
      out.push({
         text: m[1],
         variable: VARIABLES.some((v) => v.label === m![1]),
      })
      last = m.index + m[0].length
   }
   if (last < text.length) out.push({ text: text.slice(last), variable: false })
   return out
}

/** What the lead actually reads or hears. */
export const fillVariables = (
   text: string,
   lead: ISampleLead,
   org: IOrgContext,
   assistantName: string
): string =>
   text
      .split('{first name}')
      .join(lead.firstName)
      .split('{assistant name}')
      .join(assistantName)
      .split('{organization}')
      .join(org.organization)
      .split('{city}')
      .join(lead.city)
      .split('{agent name}')
      .join(org.agentName)

/** SMS segments: 160 GSM characters, 153 per part once split. Filled text decides. */
export const smsSegments = (filled: string): number =>
   filled.length <= 160 ? 1 : Math.ceil(filled.length / 153)

/** "09:30" → "9:30 am". */
export const formatTime = (hhmm: string): string => {
   const [h, m] = hhmm.split(':').map(Number)
   const suffix = h >= 12 ? 'pm' : 'am'
   const hour = h % 12 === 0 ? 12 : h % 12
   return `${hour}:${String(m).padStart(2, '0')} ${suffix}`
}

/** "09:30" → "9:30" (phone clock). */
export const clockTime = (hhmm: string): string => {
   const [h, m] = hhmm.split(':').map(Number)
   return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')}`
}

export const plural = (n: number, one: string, many = `${one}s`) =>
   `${n} ${n === 1 ? one : many}`

/** Inserts a variable at the cursor of a textarea value. */
export const insertAt = (
   value: string,
   start: number,
   end: number,
   insert: string
) => value.slice(0, start) + insert + value.slice(end)
